import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import classNames from "classnames";
import { Button } from "@flixbus/honeycomb-react";
import { Icon, IconPlus } from "@flixbus/honeycomb-icons-react";
import CompositionChip from "../CompositionChip/CompositionChip.component";
import {
  formulaDropAreaId,
  END_INSERT,
  type FormulaChipModel,
} from "../FormulaEditor.types";
import styles from "./CompositionArea.module.scss";

interface CompositionAreaProps {
  chips: FormulaChipModel[];
  isCustom: boolean;
  insertionBarPosition: string | null;
  isOver: boolean;
  onRemove: (uid: string) => void;
  onClearAll: () => void;
}

const CompositionArea: React.FC<CompositionAreaProps> = ({
  chips,
  isCustom,
  insertionBarPosition,
  isOver,
  onRemove,
  onClearAll,
}) => {
  const { setNodeRef } = useDroppable({ id: formulaDropAreaId });
  const isEmpty = chips.length === 0;
  const label = isCustom ? "Custom formula" : "Formula";

  return (
    <div>
      <div className={styles.compositionLabelRow}>
        <span className={styles.compositionLabel}>{label}</span>
        {!isEmpty && (
          <Button appearance="link" size="sm" onClick={onClearAll}>
            Clear all
          </Button>
        )}
      </div>
      <SortableContext
        items={chips.map((c) => c.token.uid)}
        strategy={rectSortingStrategy}
      >
        <div
          ref={setNodeRef}
          className={classNames(styles.composition, {
            [styles.compositionEmpty]: isEmpty,
            [styles.compositionOver]: isOver,
          })}
        >
          {isEmpty ? (
            <div className={styles.emptyState}>
              <Icon InlineIcon={IconPlus} size={4} aria-hidden="true" />
              <span>
                {"Drag operators and variables here to build your formula"}
              </span>
            </div>
          ) : (
            <>
              {chips.map((chip) => (
                <CompositionChip
                  key={chip.token.uid}
                  model={chip}
                  showInsertBar={insertionBarPosition === chip.token.uid}
                  onRemove={onRemove}
                />
              ))}
              {insertionBarPosition === END_INSERT && (
                <span className={styles.endInsertBar} aria-hidden="true" />
              )}
            </>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export default CompositionArea;
